import { NEW_LOOK_RADIUS } from "common/NewLook/tokens.ts";
import { CircularProgressbar } from "react-circular-progressbar";
import styled, { useTheme } from "styled-components";
import React, { CSSProperties, memo, useMemo } from "react";
import { round } from "@shared/utils/numbers";
import Text from "common/Typography/Text";
import { hexToRGB } from "@shared/utils/misc";
import { TradeData, TradeGraphData } from "@shared/types";
import { FontSizesValue } from "@shared/common/Themes/typography";
import { usePnlSocketData } from "features/trades/TradesCards/PnlSocketDataProvider.tsx";
import { v4 } from "uuid";

export type GraphSize = "s" | "l";
interface TradeProgressGraphProps {
  graphData?: TradeGraphData;
  tradeData?: TradeData;
  size: GraphSize;
  style?: CSSProperties;
}

const TradeProgressGraph = ({
  graphData: propsGraphData,
  tradeData,
  size,
  style,
}: TradeProgressGraphProps) => {
  const appTheme = useTheme();
  const socketData = usePnlSocketData();
  const graphData =
    propsGraphData ||
    ((socketData || tradeData)?.graph_data as TradeGraphData | undefined);
  const uuid = useMemo(() => v4(), []);

  let profit = round(graphData?.profit || 0, 2);
  let multiply = profit < 0 ? -1 : 1;
  const maxProfit = 9999;
  profit = Math.abs(profit) > maxProfit ? multiply * maxProfit : profit;
  const entryProgress = round(graphData?.entry_progress || 0, 4);
  const takeProfitProgress = round(graphData?.take_profit_progress || 0, 4);
  const timePassed = graphData?.time_passed;

  const outerGradientId = `${uuid}_entry`;
  const innerGradientId = `${uuid}_takeProfit`;

  const { isNewLook } = appTheme;

  const getBaseStyles = (isInner: boolean, isTrail: boolean): CSSProperties => {
    let strokeColor = hexToRGB(appTheme.colors.secondaryGrey, 0.14);
    if (!isTrail) {
      strokeColor = isInner
        ? `url(#${innerGradientId})`
        : `url(#${outerGradientId})`;
    }
    const innerStrokeWidth = size === "s" ? 8 : 12;
    const outerStrokeWidth = size === "s" ? 4 : 3;
    if (isNewLook) {
      // Thinner rings with rounded ends and solid colors.
      if (isTrail) {
        strokeColor = hexToRGB(appTheme.colors.secondaryGrey, 0.12);
      } else {
        strokeColor = isInner ? appTheme.colors.orange : appTheme.colors.green;
      }
      return {
        strokeLinecap: "round",
        strokeWidth: isInner ? (size === "s" ? 7 : 9) : size === "s" ? 4 : 3,
        stroke: strokeColor,
        transform: "rotate(0.62turn)",
        transformOrigin: "center center",
      };
    }
    return {
      strokeLinecap: "butt",
      strokeWidth: isInner ? innerStrokeWidth : outerStrokeWidth,
      stroke: strokeColor,
      transform: "rotate(0.62turn)",
      transformOrigin: "center center",
    };
  };

  let fontSize: FontSizesValue = appTheme.typography.fontSizes.XL;
  if (Math.abs(profit) >= 1000) {
    fontSize = appTheme.typography.fontSizes.M;
  } else if (Math.abs(profit) >= 100) {
    fontSize = appTheme.typography.fontSizes.L;
  }

  const textStyles = {
    fill: appTheme.colors.primaryText,
    fontWeight: appTheme.typography.fontWeights.MEDIUM,
    fontSize,
    ...(isNewLook && {
      fill: appTheme.colors.titleColor,
      fontWeight: appTheme.typography.fontWeights.BOLD,
      ...(size === "s" && { fontSize: getNewLookSmallFontSize(profit) }),
    }),
  };

  const baseProps = {
    minValue: 0,
    maxValue: 1,
    circleRatio: 0.76,
  };

  const outer = useMemo(() => {
    return (
      <div>
        <CircularProgressbar
          key={`${uuid}_entry`}
          value={entryProgress}
          {...baseProps}
          styles={{
            path: getBaseStyles(false, false),
            trail: getBaseStyles(false, true),
          }}
        />
      </div>
    );
  }, [entryProgress, size, appTheme]);

  const inner = useMemo(() => {
    return (
      <InnerCircleWrapper $size={size} $isNewLook={isNewLook}>
        <CircularProgressbar
          key={`${uuid}_tp`}
          value={takeProfitProgress}
          {...baseProps}
          text={`${profit}%`}
          styles={{
            path: getBaseStyles(true, false),
            trail: getBaseStyles(true, true),
            text: textStyles,
          }}
        />
      </InnerCircleWrapper>
    );
  }, [takeProfitProgress, size, appTheme, fontSize, profit]);

  const time = useMemo(() => {
    return (
      <TimePassedWrapper as="div" $graphSize={size} $isNewLook={isNewLook}>
        {timePassed}
      </TimePassedWrapper>
    );
  }, [timePassed, size, isNewLook]);

  return (
    <Wrapper $size={size} style={style} $isNewLook={isNewLook}>
      <GradientSvg gradientId={outerGradientId} />
      <GradientSvg gradientId={innerGradientId} />
      {outer}
      {inner}
      {time}
    </Wrapper>
  );
};

export default React.memo(TradeProgressGraph);

const Wrapper = styled.div<{ $size: GraphSize; $isNewLook?: boolean }>`
  display: flex;
  justify-content: center;
  margin: 0 auto;
  position: relative;
  ${(p) =>
    p.$size === "s"
      ? `
    width: 72px;
    height: 72px;
  `
      : `
    width: 200px;
    height: 200px;
  `}
  svg {
    overflow: visible;
  }
  ${(p) =>
    p.$isNewLook &&
    p.$size === "s" &&
    `
    width: 54px;
    height: 54px;
  `}
`;
const InnerCircleWrapper = styled.div<{
  $size: GraphSize;
  $isNewLook?: boolean;
}>`
  ${(p) =>
    p.$size === "s"
      ? `
    width: 59px;
    height: 59px;
  `
      : `
    width: 155px;
    height: 155px;
  `}
  position: absolute;
  left: 50%;
  bottom: ${(p) => (p.$size === "s" ? 6 : 23)}px;
  transform: translateX(-50%);
  ${(p) =>
    p.$isNewLook &&
    p.$size === "s" &&
    `
    width: 42px;
    height: 42px;
    bottom: 6px;
  `}
`;
const TimePassedWrapper = styled(Text).attrs<{
  $graphSize: GraphSize;
  $isNewLook?: boolean;
}>((p) => ({
  $size:
    p.$graphSize === "s"
      ? p.theme.typography.fontSizes.XS
      : p.theme.typography.fontSizes.L,
  $weight: p.theme.typography.fontWeights.MEDIUM,
}))`
  position: absolute;
  bottom: ${(p) => (p.$graphSize === "s" ? 0 : 10)}px;
  left: 50%;
  transform: translateX(-50%);
  background-color: ${(p) => hexToRGB(p.theme.colors.secondaryGrey, 0.1)};
  color: ${(p) => p.theme.colors.mediumText};
  border-radius: ${(p) => (p.$graphSize === "s" ? 4 : 12)}px;
  ${(p) =>
    p.$graphSize === "s"
      ? `
      padding: 2px 5px;
  `
      : `
    width: 76px;
    height: 36px;
  `}
  display: flex;
  align-items: center;
  justify-content: center;
  ${(p) =>
    p.$isNewLook &&
    `
    background-color: ${p.theme.colors.cardLightBgColor};
    color: ${p.theme.colors.secondaryText};
    border-radius: ${NEW_LOOK_RADIUS.chip}px;
    font-weight: ${p.theme.typography.fontWeights.BOLD};
  `}
  ${(p) =>
    p.$isNewLook &&
    p.$graphSize === "s" &&
    `
    bottom: -2px;
    padding: 0 6px;
    font-size: 10px;
    line-height: 15px;
  `}
`;

// SVG user units (the gauge viewBox is 100 wide), sized for the compact gauge.
const getNewLookSmallFontSize = (profit: number) => {
  const length = `${profit}%`.length;
  return `${Math.min(24, Math.floor(112 / length))}px`;
};

interface GradientSvgProps {
  gradientId: string;
}

const GradientSvg = memo(({ gradientId }: GradientSvgProps) => {
  const appTheme = useTheme();

  let startColor: string;
  let endColor: string;
  if (gradientId.includes("entry")) {
    startColor = appTheme.colors.lightGreen;
    endColor = appTheme.colors.green;
  } else {
    startColor = appTheme.colors.gold;
    endColor = appTheme.colors.red;
  }

  return (
    <svg style={{ height: 0, width: 0 }}>
      <defs>
        <linearGradient
          key={gradientId}
          id={gradientId}
          gradientTransform={"rotate(-10)"}
        >
          <stop offset="0%" stopColor={endColor} />
          <stop offset="100%" stopColor={startColor} />
        </linearGradient>
      </defs>
    </svg>
  );
});
